import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { potsService } from '@/services/potsService';
import type { CreatePotInput, UpdatePotInput } from '@/types/pot';

export function usePots() {
  return useQuery({
    queryKey: ['pots'],
    queryFn: () => potsService.getPots(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function usePot(id: string) {
  return useQuery({
    queryKey: ['pots', id],
    queryFn: () => potsService.getPot(id),
    enabled: !!id,
  });
}

export function useCreatePot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePotInput) => potsService.createPot(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pots'] });
    },
  });
}

export function useUpdatePot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePotInput }) =>
      potsService.updatePot(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pots'] });
    },
  });
}

export function useDeletePot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => potsService.deletePot(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pots'] });
    },
  });
}

export function useSetDefaultPot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => potsService.setDefaultPot(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pots'] });
    },
  });
}

export default {
  usePots,
  usePot,
  useCreatePot,
  useUpdatePot,
  useDeletePot,
  useSetDefaultPot,
};
